(ns manage.product-selector-page.sections.prioritisation
  (:require ["@dnd-kit/core" :as dnd-core]
            ["@dnd-kit/sortable" :as dnd-sortable]
            ["@dnd-kit/utilities" :as dnd-utilities]
            ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Stack$default" :as Stack]
            ["react-hook-form" :as rhf]
            [cljs-bean.core :refer [->clj ->js]]
            [clojure.string :as str]
            [common.form :as form]
            [common.util :refer [spread-props]]
            [common.widgets.form-field :refer [form-field]]
            [manage.product-selector-page.hooks :as hooks]
            [manage.product-selector-page.widgets :as widgets]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defui]]))

(def dynamic-property-options
  [{:value "user-most-viewed" :label "Most viewed products"}
   {:value "user-last-viewed" :label "Last viewed products"}
   {:value "user-last-basketed" :label "Last basketed products"}
   {:value "user-last-purchased" :label "Last purchased products"}])

(defui drag-handle [{:keys [listeners attributes]}]
  ($ Icon (spread-props #js {:sx #js {:cursor "grab"}}
                        attributes
                        listeners)
     "drag_indicator"))

(defui dynamic-property
  [{:keys [control field-name]}]
  ($ form-field
     {:label "Dynamic property"}
     ($ form/autocomplete-field
        {:control control
         :name field-name
         :field-props {:options dynamic-property-options}})))

(defui lookback-days
  [{:keys [control field-name]}]
  ($ form-field
     {:label "Last # of days"}
     ($ form/form-text-field
        {:control control
         :name field-name
         :on-change-fn js/parseInt
         :field-props {:type "number"
                       :inputProps #js {:min 1
                                        :max 30}}})))

(defui max-item-count
  [{:keys [control field-name]}]
  ($ form-field
     {:label "Max # of products"}
     ($ form/form-text-field
        {:control control
         :name field-name
         :on-change-fn js/parseInt
         :field-props {:type "number"
                       :inputProps #js {:min 1
                                        :max 10}}})))

(defui dynamic-priority-clause
  [{:keys [control field-name]}]
  ($ Box {:sx #js {:display "grid"
                   :gridTemplateColumns "repeat(3, 1fr)"
                   :gap 1}}
     ($ dynamic-property {:control control :field-name (str field-name ".type")})
     ($ lookback-days {:control control :field-name (str field-name ".lookback-days")})
     ($ max-item-count {:control control :field-name (str field-name ".max-item-count")})))

(defui manual-priority-clause
  [{:keys [control field-name]}]
  ($ form-field
     {:label "SKU"
      :sx #js {:flex 1}}
     ($ rhf/Controller
        {:control control
         :name (str field-name ".sku")
         :render (fn [^js props]
                   (let [{{:keys [value]} :field} (->clj props)]
                     ($ :span value)))})))

(defui priority-clause [{:keys [id type control fieldName onDelete]}]
  (let [sortable (dnd-sortable/useSortable #js {:id id})
        setNodeRef (.-setNodeRef sortable)
        attributes (.-attributes sortable)
        listeners (.-listeners sortable)
        transform (.-transform sortable)
        transition (.-transition sortable)
        is-dragging (.-isDragging sortable)]
    ($ Paper
       {:ref setNodeRef
        :component Stack
        :elevation 0
        :sx #js {:overflow "hidden"
                 :transform (dnd-utilities/CSS.Transform.toString transform)
                 :transition transition
                 :zIndex (if is-dragging 1200 "auto")}}
       ($ Stack
          {:direction "row"
           :justifyContent "space-between"
           :alignItems "center"
           :sx #js {:py 0.5
                    :px 2
                    :backgroundColor "grey.70"}}
          ($ drag-handle {:listeners listeners
                          :attributes attributes})
          ($ IconButton
             {:onClick onDelete}
             ($ Icon "delete")))
       ($ Box {:sx #js {:px 2 :py 1.5}}
          (if (= type "sku")
            ($ manual-priority-clause {:control control :field-name fieldName})
            ($ dynamic-priority-clause {:control control :field-name fieldName}))))))

(defui prioritization-section-body [{:keys [items control fieldName onDelete move]}]
  (let [sensors (dnd-core/useSensors
                 (dnd-core/useSensor dnd-core/PointerSensor)
                 (dnd-core/useSensor dnd-core/KeyboardSensor
                                     #js {:coordinateGetter dnd-sortable/sortableKeyboardCoordinates}))
        item-ids (->js (mapv (fn [item] (.-id item)) items))

        handleDragEnd (uix/use-callback
                       (fn [^js event]
                         (let [active-id (-> event .-active .-id)
                               over-id (-> event .-over .-id)]
                           (when (not= active-id over-id)
                             (let [old-index (.indexOf item-ids active-id)
                                   new-index (.indexOf item-ids over-id)]
                               (move old-index new-index)))))
                       [item-ids move])]
    ($ dnd-core/DndContext
       {:sensors sensors
        :collisionDetection dnd-core/closestCenter
        :onDragEnd handleDragEnd}
       ($ dnd-sortable/SortableContext
          {:items item-ids
           :strategy dnd-sortable/verticalListSortingStrategy}
          ($ Stack {:gap 2}
             (for [[idx id] (mc/seq-indexed item-ids)]
               (let [item (.find items (fn [item] (= (.-id item) id)))]
                 ($ priority-clause
                    {:key id
                     :id id
                     :type (.-type item)
                     :control control
                     :fieldName (str fieldName "[" idx "]")
                     :onDelete #(onDelete idx)}))))))))

(defui edit []
  (let [{:keys [control]} (form/use-form-context)
        field-name "priority"
        {:keys [fields append remove move]} (form/use-field-array #js {:control control
                                                                       :name field-name})
        prioritised-products (->> (->clj fields)
                                  (keep #(when (:sku %) %)))]
    ($ Stack
       {:gap 2}
       (when (seq fields)
         ($ prioritization-section-body
            {:items fields
             :control control
             :fieldName field-name
             :onDelete #(remove %)
             :move move}))
       ($ Stack
          {:direction "row"
           :gap 1}
          ($ Button
             {:variant "text"
              :startIcon ($ Icon "add")
              :onClick #(append (->js hooks/initial-dynamic-priority-clause))
              :sx #js {:alignSelf "flex-start"}}
             "Add dynamic property")
          ($ Button
             {:variant "text"
              :startIcon ($ Icon "add")
              :onClick (fn []
                         (rf/dispatch
                          [:modal/open {:id :product-modal
                                        :props {:on-select (fn [p]
                                                             (append #js {:type "sku" :sku (:sku p)})
                                                             (rf/dispatch [:modal/close]))
                                                :disabled-products prioritised-products}}]))
              :sx #js {:alignSelf "flex-start"}}
             "Add manual item")))))

(defui read-dynamic-priority-clause [{:keys [priority]}]
  (let [{:keys [type max-item-count lookback-days]} priority
        type->label (->> dynamic-property-options
                         (map (juxt :value :label))
                         (into {}))]
    ($ form-field
       {:label (get type->label type)}
       ($ :span (str "Last " lookback-days " days"))
       ($ :span (str max-item-count " products maximum")))))

(defui read []
  (let [{:keys [control]} (form/use-form-context)
        priority (rhf/useWatch #js {:name "priority" :control control})
        {manual-items true dynamic-items false} (group-by #(= (:type %) "sku") (->clj priority))]
    ($ Stack {:gap 2}
       (for [[idx item] (mc/seq-indexed dynamic-items)]
         ($ read-dynamic-priority-clause {:key idx :priority item}))
       (when (seq manual-items)
         ($ form-field
            {:label "Manual products"}
            (str/join ", " (map :sku manual-items))))
       (when (empty? priority)
         ($ widgets/empty-section-message {:step-label "product prioritisation"})))))

(defui prioritisation []
  (let [{:keys [mode]} (form/use-form-context)]
    ($ widgets/section-card
       {:title "Prioritise products"
        :tooltip "Decide which products should take priority. Pick them manually or
                  use event-based rules dynamic properties, like “last viewed”."}
       (if (= mode :read)
         ($ read)
         ($ edit)))))
